<script setup lang="ts">
import { computed, onMounted } from "vue";
// import { Fold } from "@element-plus/icons-vue";
import { Slotted } from "~/types/Slotted";
import {
  WorkflowDto,
  WorkflowMetaCode,
  WorkflowStatus,
  QuestionCodesToDisplay,
  FormElementType,
} from "~/api";
import { formatDate } from "~/utils/dateUtils";
import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { useLoginAccountsStore } from "~/stores/LoginAccountsStore";
import { splitByCaps } from "~/utils/miscUtils";

const props = defineProps<{
  /** The workflow rows to display */
  data: WorkflowDto[];
  /** Column definitions from WorkflowsDto.questionCodesToDisplay */
  columns: QuestionCodesToDisplay[];
  /** Status list from WorkflowsDto.statusList - used for status label resolution */
  statusList: WorkflowStatus[];
  /** Entity name lookup from WorkflowsDto.entitiesMentioned */
  entitiesMentioned: Record<string, string> | null;
  /** Single form response statuses from WorkflowsDto.singleFormResponseStatuses */
  singleFormResponseStatuses: Record<string, string> | null;
  /** Whether to show the loading spinner */
  loading?: boolean;
  /** Whether to show the archived-at column */
  showArchivedColumn?: boolean;
  /** Whether to show the linked housing application column */
  showHousingApplicationColumn?: boolean;
}>();

const emit = defineEmits<{
  (e: "workflow-click", workflow: WorkflowDto): void;
}>();

const orgInfoStore = useOrgInfoStore();
const loginAccountsStore = useLoginAccountsStore();

onMounted(async () => {
  await loginAccountsStore.load();
});

const hasNameColumn = computed(() =>
  props.columns.some((q) => q.metaCode === WorkflowMetaCode.NAME),
);

const getWorkflowStatusLabel = (statusCode: string) => {
  return props.statusList?.find((status) => status.code === statusCode)?.label;
};

const getEntityName = (entityGuid: string): string | undefined => {
  return props.entitiesMentioned?.[entityGuid] ?? entityGuid;
};

const getColumnValue = (wf: WorkflowDto, question: QuestionCodesToDisplay) => {
  switch (question.metaCode) {
    case WorkflowMetaCode.FOR:
      return wf.ownerName;
    case WorkflowMetaCode.STATUS:
      return getWorkflowStatusLabel(wf.workflowStatus) ?? wf.workflowStatus;
    case WorkflowMetaCode.ARCHIVED_AT:
      return formatDate(wf.workflowArchivedAt, 1);
    case WorkflowMetaCode.UPDATED_AT:
      return formatDate(wf.updatedAt, 1);
  }

  const rawValue = wf.answersToShow[question.questionCode!] as
    | string
    | undefined;
  if (!rawValue) {
    return "";
  }
  const plainValue = rawValue.replace(/['"]+/g, "");

  switch (question.type) {
    case FormElementType.DATE:
      return rawValue ? formatDate(rawValue) : "N/A";
    case FormElementType.BOOLEAN:
      return rawValue === "true" ? "Yes" : "No";
    case FormElementType.NUMBER:
      return rawValue ? parseFloat(rawValue).toLocaleString() : "N/A";
    case FormElementType.ENTITY:
      return getEntityName(plainValue);
    case FormElementType.SECURITY_GROUP:
      return (
        orgInfoStore.MyOrgInfo?.orgGroupDtos?.find((g) => g.guid === plainValue)
          ?.name ?? "N/A"
      );
    case FormElementType.USER_SELECT:
      return loginAccountsStore.getName(plainValue);
    case FormElementType.SELECT_LIST:
      return splitByCaps(plainValue);
    case FormElementType.ACTIVITY_DATE: {
      try {
        const parsed = JSON.parse(rawValue);
        return parsed.computedDateTime
          ? formatDate(parsed.computedDateTime, 1)
          : "N/A";
      } catch {
        return rawValue ? formatDate(rawValue, 1) : "N/A";
      }
    }
    default:
      return rawValue?.replace(/['"]+/g, "") || "N/A";
  }
};
</script>

<template>
  <el-table
    v-loading="loading ?? false"
    :data="data"
    style="width: 100%"
    sortable
    aria-label="Workflows table"
  >
    <el-table-column
      v-if="showHousingApplicationColumn"
      label="Housing Application"
      width="200"
    >
      <template #default="{ row }: Slotted<WorkflowDto>">
        {{ row.linkedHousingApplicationName ?? "-" }}
      </template>
    </el-table-column>
    <el-table-column
      v-for="question in columns"
      :key="question.metaCode || question.guid || ''"
      :width="question.width ?? undefined"
      :label="question.label ?? undefined"
    >
      <template #default="{ row }: Slotted<WorkflowDto>">
        <el-link
          v-if="
            question.metaCode === WorkflowMetaCode.NAME ||
            (!hasNameColumn && question === columns[0])
          "
          underline="never"
          :aria-label="`View workflow ${row.workflowName}`"
          @click="emit('workflow-click', row)"
        >
          {{
            question.metaCode === WorkflowMetaCode.NAME
              ? row.workflowName
              : getColumnValue(row, question)
          }}
        </el-link>
        <span v-else>
          {{ getColumnValue(row, question) }}
          <span
            v-if="
              question.metaCode === WorkflowMetaCode.STATUS &&
              singleFormResponseStatuses?.[row.guid!]
            "
            class="form-status"
          >
            {{ singleFormResponseStatuses![row.guid!] }}
          </span>
        </span>
      </template>
    </el-table-column>
    <el-table-column v-if="showArchivedColumn" label="Archived At" width="180">
      <template #default="{ row }: Slotted<WorkflowDto>">
        {{ row.workflowArchivedAt ? formatDate(row.workflowArchivedAt) : "-" }}
      </template>
    </el-table-column>
  </el-table>
</template>
